import { FC, useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Box,
  Button,
  Flex,
  Link,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
  Card,
  CardBody,
  Spinner,
} from "@chakra-ui/react";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { StringInput, SelectInput } from "~/components";
import { useLazyFacilitiesSearchQuery } from "~/store/api";
import type { Facility } from "sdk/labTests";

interface Props {
  productSlug: string;
  onProceed: () => void;
}

export const FacilityFinderForm: FC<Props> = ({ productSlug, onProceed }) => {
  const { t } = useTranslation();
  const [hasSearched, setHasSearched] = useState(false);
  const [searchFacilities, { data: facilities = [], isLoading, error }] =
    useLazyFacilitiesSearchQuery();

  const getGoogleMapsUrl = (facility: Facility) => {
    const address = [
      ...facility.address.lines,
      facility.address.city,
      facility.address.state,
      facility.address.postalCode,
    ].join(", ");

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const radiusOptions = [
    { value: "10", label: t("diagnosticDashboard.patientForm.facilityFinder.radiusOptions.ten") },
    { value: "20", label: t("diagnosticDashboard.patientForm.facilityFinder.radiusOptions.twenty") },
    { value: "25", label: t("diagnosticDashboard.patientForm.facilityFinder.radiusOptions.twentyFive") },
    { value: "50", label: t("diagnosticDashboard.patientForm.facilityFinder.radiusOptions.fifty") },
  ];

  const validationSchema = Yup.object({
    postalCode: Yup.string()
      .required(t("diagnosticDashboard.patientForm.facilityFinder.validation.postalCodeRequired"))
      .matches(/^\d{5}$/, t("diagnosticDashboard.patientForm.facilityFinder.validation.postalCodeInvalid")),
    radius: Yup.string().required(t("diagnosticDashboard.patientForm.facilityFinder.validation.radiusRequired")),
  });

  const formik = useFormik({
    initialValues: {
      postalCode: "",
      radius: "50",
    },
    validationSchema,
    onSubmit: () => {
      // Not used - auto-submits on valid input
    },
  });

  // Auto-search when ZIP code is valid (5 digits)
  useEffect(() => {
    const isValidZip = /^\d{5}$/.test(formik.values.postalCode);

    if (isValidZip) {
      const performSearch = async () => {
        try {
          await searchFacilities({
            postal_code: formik.values.postalCode,
            product_slug: productSlug,
            radius: formik.values.radius,
          });
          setHasSearched(true);
        } catch (err) {
          console.error("Error fetching facilities:", err);
        }
      };

      performSearch();
    }
  }, [formik.values.postalCode, formik.values.radius, productSlug, searchFacilities]);

  return (
    <FormikProvider value={formik}>
      <VStack
        w="full"
        alignItems="flex-start"
        spacing={6}
      >
        <Text fontSize="2xl" fontWeight="bold">
          {t("diagnosticDashboard.patientForm.facilityFinder.heading")}
        </Text>

        <Flex gap={4} width="100%" direction={{ base: "column", md: "row" }}>
          <Box flex={2} position="relative">
            <StringInput
              id="postalCode"
              label={t("diagnosticDashboard.patientForm.facilityFinder.zipCodeLabel")}
              placeholder={t("diagnosticDashboard.patientForm.facilityFinder.zipCodePlaceholder")}
              maxLength={5}
            />
            {isLoading && formik.values.postalCode.length === 5 && (
              <Spinner
                size="sm"
                position="absolute"
                right="12px"
                top="42px"
                color="th.actionLavender2"
              />
            )}
          </Box>

          <Box flex={1}>
            <SelectInput
              id="radius"
              label={t("diagnosticDashboard.patientForm.facilityFinder.radiusLabel")}
              options={radiusOptions}
            />
          </Box>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              {t("diagnosticDashboard.patientForm.facilityFinder.errorFetchingFacilities")}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && hasSearched && facilities.length === 0 && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold" mb={1}>
                {t("diagnosticDashboard.patientForm.facilityFinder.noFacilitiesFoundTitle", { radius: formik.values.radius, postalCode: formik.values.postalCode })}
              </Text>
              <Text fontSize="sm">
                {t("diagnosticDashboard.patientForm.facilityFinder.noFacilitiesFoundDescription")}
              </Text>
            </Box>
          </Alert>
        )}

        {!isLoading && facilities.length > 0 && (
          <VStack width="100%" spacing={4} alignItems="stretch">
            <Text fontSize="md" fontWeight="medium" color="gray.700">
              {t("diagnosticDashboard.patientForm.facilityFinder.locationsFoundNear", { count: facilities.length })}
            </Text>

            <VStack spacing={3} maxH="400px" overflowY="auto" pr={2}>
              {facilities.map((facility, index) => (
                <Card
                  key={index}
                  as={Link}
                  href={getGoogleMapsUrl(facility)}
                  isExternal
                  width="100%"
                  variant="outline"
                  borderColor="gray.200"
                  _hover={{
                    borderColor: "th.actionLavender2",
                    boxShadow: "sm",
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                  transition="all 0.2s"
                  textDecoration="none"
                >
                  <CardBody>
                    <VStack alignItems="flex-start" spacing={2}>
                      <Flex justify="space-between" width="100%">
                        <Text fontWeight="bold" fontSize="md">
                          {facility.name}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {facility.distance.toFixed(1)} mi
                        </Text>
                      </Flex>

                      <Text fontSize="sm" color="gray.700">
                        {facility.address.lines.join(", ")}
                        <br />
                        {facility.address.city}, {facility.address.state}{" "}
                        {facility.address.postalCode}
                      </Text>

                      <Flex gap={4} fontSize="sm" color="gray.600">
                        {facility.telephone && (
                          <Text>{t("diagnosticDashboard.patientForm.facilityFinder.phoneLabel")} {facility.telephone}</Text>
                        )}
                        {facility.faxNumber && (
                          <Text>{t("diagnosticDashboard.patientForm.facilityFinder.faxLabel")} {facility.faxNumber}</Text>
                        )}
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>

            <Box
              bg="blue.50"
              p={4}
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="blue.400"
            >
              <Text fontSize="md" color="gray.800">
                <Trans
                  i18nKey="diagnosticDashboard.patientForm.facilityFinder.summaryMessage"
                  count={facilities.length}
                  components={{ strong: <strong /> }}
                />
              </Text>
            </Box>

            <Button
              onClick={onProceed}
              size="th-md"
              variant="th-primary"
              width="100%"
            >
              {t("diagnosticDashboard.patientForm.facilityFinder.continueButton")}
            </Button>
          </VStack>
        )}
      </VStack>
    </FormikProvider>
  );
};
